import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useMemo, useState } from 'react';
import { AlertTriangle, ChevronRight, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  getExpiringCredentials,
  EXPIRY_WINDOW_DAYS,
  formatDaysDelta,
} from '../utils/employeeExpiry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Urgency chips presented in the widget header. `predicate(days)` is
 * evaluated against a signed day-delta (negative = already expired).
 */
const URGENCY_CHIPS = [
  { key: 'all',     label: i18n.t('auto.lbl.all'),       predicate: () => true },
  { key: 'expired', label: i18n.t('auto.lbl.expired'),   predicate: (d) => d < 0 },
  { key: '30d',     label: i18n.t('auto.lbl.next30d'),  predicate: (d) => d >= 0 && d <= 30 },
  { key: '90d',     label: i18n.t('auto.lbl.next90d'),  predicate: (d) => d >= 0 && d <= 90 },
];

/**
 * Dashboard widget listing every expiring or already-expired
 * credential, one row per (employee × credential) pair, ordered by
 * urgency (most negative days first). Clicking any row deep-links to
 * that employee's edit dialog on the Employees tab.
 *
 * Fully client-side; consumes the shared `employees` array already
 * loaded by the Dashboard so no extra API calls.
 */
export default function CredentialsExpiryWidget({ employees, onNavigate }) {
  const { t } = useTranslation();
  const [chip, setChip] = useState('all');
  // 'urgency' groups by soonest-to-expire first. 'name' groups all
  // credentials belonging to the same employee next to one another —
  // useful when copying a per-person nag-list.
  const [sortMode, setSortMode] = useState('urgency');
  // 'flat' shows every (employee × credential) as its own row.
  // 'grouped' collapses per employee — one summary row per person with
  // an expandable chevron to peek at their credentials inline.
  const [viewMode, setViewMode] = useState('flat');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  // Full flat list of every expiring/expired credential across the
  // team. Sorted according to `sortMode` — either urgent-first
  // (default) or by employee name then urgency-within-employee.
  const allRows = useMemo(() => {
    const flat = [];
    (employees || []).forEach((e) => {
      getExpiringCredentials(e, EXPIRY_WINDOW_DAYS).forEach((item) => {
        flat.push({ employee: e, ...item });
      });
    });
    const sorter =
      sortMode === 'name'
        ? (a, b) => {
            const na = (a.employee.name || a.employee.employee_code || '').toLowerCase();
            const nb = (b.employee.name || b.employee.employee_code || '').toLowerCase();
            if (na !== nb) return na.localeCompare(nb);
            return a.days - b.days;
          }
        : (a, b) => a.days - b.days;
    return flat.sort(sorter);
  }, [employees, sortMode]);

  const activePredicate = URGENCY_CHIPS.find((c) => c.key === chip)?.predicate;
  const rows = useMemo(
    () => (activePredicate ? allRows.filter((r) => activePredicate(r.days)) : allRows),
    [allRows, activePredicate]
  );

  const chipCounts = useMemo(() => {
    const out = {};
    URGENCY_CHIPS.forEach((c) => { out[c.key] = allRows.filter((r) => c.predicate(r.days)).length; });
    return out;
  }, [allRows]);

  const totalEmployees = useMemo(
    () => new Set(rows.map((r) => r.employee.id)).size,
    [rows]
  );

  // Build the grouped-by-employee shape derived from the already
  // chip-filtered flat rows. Sort order of the outer buckets honours
  // `sortMode` — either by earliest-urgency-in-group or by name.
  const groupedRows = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const id = r.employee.id;
      if (!map.has(id)) map.set(id, { employee: r.employee, items: [] });
      map.get(id).items.push(r);
    });
    const buckets = Array.from(map.values());
    // Items inside a bucket stay urgent-first regardless of outer sort
    buckets.forEach((b) => b.items.sort((a, b2) => a.days - b2.days));
    return buckets.sort((a, b) => {
      if (sortMode === 'name') {
        const na = (a.employee.name || a.employee.employee_code || '').toLowerCase();
        const nb = (b.employee.name || b.employee.employee_code || '').toLowerCase();
        return na.localeCompare(nb);
      }
      return a.items[0].days - b.items[0].days;
    });
  }, [rows, sortMode]);

  const toggleGroup = (id) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openEmployee = (employee) => {
    onNavigate?.('employees', { filterExpiring: true, editEmployeeId: employee.id });
  };

  const jumpToFiltered = () => onNavigate?.('employees', { filterExpiring: true });

  const [copied, setCopied] = useState(false);
  const activeChipLabel = URGENCY_CHIPS.find((c) => c.key === chip)?.label || 'All';
  const copySummary = async () => {
    if (rows.length === 0) return;
    const lines = rows.map(({ employee, label, days }) => {
      const who = employee.name || employee.employee_code || '(unnamed)';
      return `${who} — ${label}: ${formatDaysDelta(days)}`;
    });
    const text = `Certifications expiring (${activeChipLabel}) — ${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}:\n${lines.join('\n')}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`Copied ${rows.length} entr${rows.length === 1 ? 'y' : 'ies'} to clipboard`);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error(`Copy failed: ${err?.message || 'clipboard unavailable'}`);
    }
  };

  return (
    <Card className="bg-white border-0 shadow-sm" data-testid="credentials-expiry-widget">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3">
        <div className="flex items-center gap-2 min-w-0 w-full sm:w-auto">
          <div className="w-8 h-8 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-base break-words">{t('auto.misc2.certificationsExpiringSoon')}</CardTitle>
            <p className="text-xs text-slate-500">
              {allRows.length === 0
                ? 'Nothing expiring in the next 90 days — all clear.'
                : rows.length === 0
                ? `No credentials in the "${URGENCY_CHIPS.find((c) => c.key === chip)?.label}" window.`
                : `${rows.length} credential${rows.length === 1 ? '' : 's'} across ${totalEmployees} employee${totalEmployees === 1 ? '' : 's'}${chip === 'all' ? ` lapsing in ≤ ${EXPIRY_WINDOW_DAYS}d` : ''}`}
            </p>
          </div>
        </div>
        {allRows.length > 0 && (
          <div className="flex items-center gap-1 shrink-0 self-end sm:self-auto">
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              onClick={copySummary}
              disabled={rows.length === 0}
              title={`Copy the ${activeChipLabel.toLowerCase()} list to clipboard`}
              data-testid="credentials-expiry-copy-summary"
            >
              {copied ? <Check className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? t('auto.misc2.copied') : t('auto.misc2.copy')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
              onClick={jumpToFiltered}
              data-testid="credentials-expiry-view-all"
            >
              {t('auto.misc2.viewAll')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </CardHeader>

      {allRows.length > 0 && (
        <>
          {/* Urgency triage chips — client-side filter over the flat
              list. Counts inside the pills stay fresh regardless of
              the active selection. */}
          <div className="px-6 pb-2 flex flex-wrap gap-1.5" data-testid="credentials-expiry-chips">
            {URGENCY_CHIPS.map((c) => {
              const active = c.key === chip;
              const count = chipCounts[c.key];
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setChip(c.key)}
                  data-testid={`credentials-expiry-chip-${c.key}`}
                  data-active={active}
                  className={
                    'inline-flex items-center gap-1.5 rounded-full text-xs font-medium px-2.5 py-1 border transition-colors ' +
                    (active
                      ? c.key === 'expired'
                        ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                        : 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                  }
                >
                  <span>{c.label}</span>
                  <span
                    className={
                      'inline-flex items-center justify-center min-w-[18px] rounded-full text-[10px] px-1 ' +
                      (active
                        ? 'bg-white/25 text-white'
                        : count === 0
                        ? 'bg-slate-100 text-slate-400'
                        : c.key === 'expired'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-800')
                    }
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Sort / View controls on their own row so they never fight
              the chip row for horizontal space on narrow widths. */}
          <div className="px-6 pb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-slate-500">
            <div className="flex items-center gap-1" data-testid="credentials-expiry-sort">
              <span>{t('auto.misc2.sort')}</span>
              {[
                { key: 'urgency', label: t('auto.lbl.urgency') },
                { key: 'name',    label: t('auto.lbl.name') },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSortMode(opt.key)}
                  data-testid={`credentials-expiry-sort-${opt.key}`}
                  data-active={sortMode === opt.key}
                  className={
                    'rounded-full text-xs font-medium px-2 py-0.5 border transition-colors ' +
                    (sortMode === opt.key
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span>{t('auto.misc2.view')}</span>
              {[
                { key: 'flat',    label: t('auto.lbl.flat') },
                { key: 'grouped', label: t('auto.lbl.grouped') },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setViewMode(opt.key)}
                  data-testid={`credentials-expiry-view-${opt.key}`}
                  data-active={viewMode === opt.key}
                  className={
                    'rounded-full text-xs font-medium px-2 py-0.5 border transition-colors ' +
                    (viewMode === opt.key
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {rows.length > 0 && (
        <CardContent className="pt-0">
          {/* Let the widget grow to fit its content — with the chip +
              sort/view filters the user can already trim the visible
              set. Capping the height only hid rows behind a scrollbar
              even when the Dashboard had plenty of vertical space. */}
          <ul
            className="divide-y divide-slate-100"
            data-testid="credentials-expiry-list"
            data-view={viewMode}
          >
            {viewMode === 'flat'
              ? rows.map(({ employee, field, label, days }) => (
                  <li
                    key={`${employee.id}-${field}`}
                    className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded cursor-pointer hover:bg-slate-50"
                    onClick={() => openEmployee(employee)}
                    data-testid={`credentials-expiry-row-${employee.id}-${field}`}
                  >
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage
                        src={employee.avatar_file ? `${BACKEND_URL}/api/files/avatar/${employee.avatar_file}` : undefined}
                        alt={employee.name || employee.employee_code}
                      />
                      <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                        {(employee.name || employee.employee_code)?.charAt(0)?.toUpperCase() || 'E'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {employee.name || employee.employee_code}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        <span className="font-medium text-slate-700">{label}</span>
                      </div>
                    </div>
                    <div
                      className={`text-xs font-semibold shrink-0 ${
                        days < 0
                          ? 'text-red-600'
                          : days <= 14
                          ? 'text-amber-600'
                          : 'text-slate-500'
                      }`}
                    >
                      {formatDaysDelta(days)}
                    </div>
                  </li>
                ))
              : groupedRows.map(({ employee, items }) => {
                  const expanded = expandedGroups.has(employee.id);
                  const worst = items[0].days;
                  return (
                    <li key={employee.id} data-testid={`credentials-expiry-group-${employee.id}`}>
                      <div
                        className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded cursor-pointer hover:bg-slate-50"
                        onClick={() => openEmployee(employee)}
                      >
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleGroup(employee.id); }}
                          className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"
                          title={expanded ? t('auto.misc2.collapse') : t('auto.misc2.expand')}
                          data-testid={`credentials-expiry-group-toggle-${employee.id}`}
                        >
                          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarImage
                            src={employee.avatar_file ? `${BACKEND_URL}/api/files/avatar/${employee.avatar_file}` : undefined}
                            alt={employee.name || employee.employee_code}
                          />
                          <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                            {(employee.name || employee.employee_code)?.charAt(0)?.toUpperCase() || 'E'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {employee.name || employee.employee_code}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {items.length} credential{items.length === 1 ? '' : 's'} expiring
                          </div>
                        </div>
                        <div
                          className={`text-xs font-semibold shrink-0 ${
                            worst < 0
                              ? 'text-red-600'
                              : worst <= 14
                              ? 'text-amber-600'
                              : 'text-slate-500'
                          }`}
                        >
                          {formatDaysDelta(worst)}
                        </div>
                      </div>
                      {expanded && (
                        <ul
                          className="pl-11 pb-1.5 space-y-0.5"
                          data-testid={`credentials-expiry-group-items-${employee.id}`}
                        >
                          {items.map(({ field, label, days }) => (
                            <li
                              key={field}
                              className="flex items-center gap-2 text-xs py-1"
                              data-testid={`credentials-expiry-group-item-${employee.id}-${field}`}
                            >
                              <span className="text-slate-700 flex-1 truncate">{label}</span>
                              <span
                                className={`shrink-0 font-medium ${
                                  days < 0
                                    ? 'text-red-600'
                                    : days <= 14
                                    ? 'text-amber-600'
                                    : 'text-slate-500'
                                }`}
                              >
                                {formatDaysDelta(days)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}
